import React from 'react';
import classnames from 'classnames/bind';
import { Column } from 'react-table';
import { ActionIcon, Badge, Menu } from '@mantine/core';
import { UseQueryResult } from '@tanstack/react-query';

import APIError from 'types/api_error';
import { OffsetPaginated } from 'types/pagination';
import { UserGroup } from 'types/user_group';

import { useDeleteUserGroups } from 'settings/hooks/useDeleteUserGroups';
import { useModal } from 'hooks/useModal';

import { EmptyView } from 'components/EmptyView/EmptyView';
import { Heading } from 'components/Heading/Heading';
import { Icon } from 'components/Icon/IconDeprecated';
import { PaginatedTable } from 'components/Table/PaginatedTable';

import settingsStyles from '../../../../Settings.module.scss';

const c = classnames.bind(settingsStyles);

type UserGroupsTableProps = {
  className?: string;
  queryResult: UseQueryResult<OffsetPaginated<UserGroup> | undefined, APIError>;
  pageSize: number;
  page: number;
  onPageChange: (page: number) => void;
  onEdit: (group: UserGroup) => void;
};

export function UserGroupsTable({
  className,
  queryResult,
  pageSize,
  page,
  onPageChange,
  onEdit,
}: UserGroupsTableProps) {
  const { mutate: deleteGroups } = useDeleteUserGroups();
  const { open } = useModal();

  const handleDelete = React.useCallback(
    (group: UserGroup) => {
      open('confirm-delete', {
        title: `Are you sure you want to delete "${group.name}"?`,
        onConfirm: () => deleteGroups({ user_group_ids: [group.id] }),
      });
    },
    [deleteGroups, open]
  );

  const columns = React.useMemo<Column<UserGroup>[]>(
    () => [
      {
        Header: 'Name',
        Cell({ row: { original } }) {
          return (
            <div className="flex items-center gap-2">
              <Heading level="4">{original.name}</Heading>
              {original.is_default && (
                <Badge size="sm" variant="light">
                  Default
                </Badge>
              )}
            </div>
          );
        },
      },
      {
        id: 'actions',
        width: 0,
        Cell({ row: { original } }) {
          return (
            <Menu position="bottom-end">
              <Menu.Target>
                <ActionIcon aria-label="More actions" variant="subtle">
                  <Icon name="more-vertical" unstyled />
                </ActionIcon>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Item onClick={() => onEdit(original)}>Edit</Menu.Item>
                <Menu.Item
                  color="red"
                  disabled={original.is_default}
                  onClick={() => handleDelete(original)}
                >
                  Delete
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          );
        },
      },
    ],
    [handleDelete, onEdit]
  );

  return (
    <PaginatedTable<UserGroup>
      className={c('config-table', className)}
      id="user-groups-table"
      label="User groups"
      columns={columns}
      queryResult={queryResult}
      pageSize={pageSize}
      page={page}
      onPageChange={onPageChange}
      emptyMessage={<EmptyView>No user groups yet.</EmptyView>}
    />
  );
}
